import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";

import {
  PennButton,
  PennDropdown,
  PennField,
  PennInput,
  PennModal,
  PennText,
} from "@/components/ui";
import {
  applicationFormSchema,
  type ApplicationFormData,
} from "@/schemas/application";
import {
  useApplication,
  useCreateApplication,
  useUpdateApplication,
} from "@/hooks/useApplications";
import { useFastenerCatalog } from "@/hooks/useFastenerCatalog";
import { useInstallationMaterials } from "@/hooks/useInstallationMaterials";
import {
  searchFastenerCatalog,
  type FastenerCatalogItem,
} from "@/api/catalogs";
import { AttachmentsPanel } from "@/components/attachments/AttachmentsPanel";
import { Cad3dPanel } from "@/components/cad3d/Cad3dPanel";
import {
  downloadFilesAsZip,
  downloadStagedFile,
  finalizeApplicationCrm,
} from "@/api/attachments";
import { useUploadAttachment } from "@/hooks/useAttachments";
import { useUploadCadStep } from "@/hooks/useCad3d";
import {
  CAD_FILENAME_MAX_CHARS,
  MATERIAL_MAX_CHARS,
  cadFilenameValidationMessage,
} from "@/utils/input-limits";
import { useAppSelector } from "@/stores/hooks";
import {
  readStoredWorkFor,
  workForHeaderValues,
} from "@penn/web-common";
import type {
  ApplicationDetail,
  ApplicationFastenerLineInput,
  CreateApplicationInput,
  FastenerBrandCode,
  UpdateApplicationInput,
} from "@/api/applications";

type Mode = "create" | "edit";

type ApplicationEditorModalProps = {
  open: boolean;
  onClose: () => void;
  applicationId?: string | null;
  mode?: Mode;
  onSaved?: (app: ApplicationDetail) => void;
};

const NO_MATERIALS: string[] = [];

// Brand order matches the POC application editor
// (["PEM","SHEREX","PROFIL","Autoriv","Other"]).
const BRAND_OPTIONS: Array<{ value: FastenerBrandCode; label: string }> = [
  { value: "PEM", label: "PEM" },
  { value: "SHEREX", label: "SHEREX" },
  { value: "PROFIL", label: "PROFIL" },
  { value: "AUTORIV", label: "Autoriv" },
  { value: "OTHER", label: "Other" },
];

// One blank fastener line. POC keeps at least one row in the editor at all
// times — the first/only row is never deleted, its values are just cleared.
const makeEmptyFastener = (): ApplicationFormData["fasteners"][number] => ({
  brand: "PEM",
  type: null,
  qtyPerPartA: 0,
  qtyPerPartB: 0,
});

const EMPTY_DEFAULTS: ApplicationFormData = {
  name: "",
  applicationNumber: null,
  oem: null,
  productionProgram: null,
  materialCode: null,
  annualVolume: null,
  batchSize: null,
  fasteners: [makeEmptyFastener()],
};

// C2: client-side staging — a new application has no id yet (CAD + attachments
// are stored in S3 keyed by applicationId), so in create mode the files are held
// in memory and uploaded automatically right after the first save. Limits mirror
// the real upload panels (AttachmentsPanel / Cad3dPanel).
const ATTACHMENT_MAX_BYTES = 50 * 1024 * 1024;
const CAD_ACCEPT = ".step,.stp";

function formatStagedSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function detailToFormData(app: ApplicationDetail): ApplicationFormData {
  const fasteners = app.fasteners.map((f) => {
    const a = f.qtyPerPartA ?? f.qtyPerPart ?? 0;
    const b = f.qtyPerPartB ?? 0;
    return {
      fastenerId: f.fastenerId,
      brand: (f.brand ?? "OTHER") as FastenerBrandCode,
      type: f.type,
      qtyPerPartA: a,
      qtyPerPartB: b,
    };
  });
  return {
    name: app.name ?? "",
    applicationNumber: app.applicationNumber,
    oem: app.oem,
    productionProgram: app.productionProgram,
    materialCode: app.materialCode,
    annualVolume: app.annualVolume,
    batchSize: app.batchSize,
    // Always keep at least one row visible (POC parity).
    fasteners: fasteners.length > 0 ? fasteners : [makeEmptyFastener()],
  };
}

function describeError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { error?: { message?: string } }
      | undefined;
    return data?.error?.message ?? "Failed to save application.";
  }
  return "Failed to save application.";
}

const labelForBrand = (brand: FastenerBrandCode): string =>
  BRAND_OPTIONS.find((b) => b.value === brand)?.label ?? brand;

// C4: stable signature of the editable application payload used to detect whether
// an edited saved application actually changed (POC canonicalizeAppPayload /
// appPayloadEquals). Ignores fastenerId and the
// react-hook-form field-array keys; Type is upper-cased to match catalog casing.
function canonicalizeForm(d: ApplicationFormData): string {
  return JSON.stringify({
    name: (d.name ?? "").trim(),
    applicationNumber: (d.applicationNumber ?? "").trim(),
    oem: (d.oem ?? "").trim(),
    productionProgram: (d.productionProgram ?? "").trim(),
    materialCode: (d.materialCode ?? "").trim(),
    annualVolume: d.annualVolume ?? null,
    batchSize: d.batchSize ?? null,
    fasteners: d.fasteners.map((f) => ({
      brand: f.brand,
      type: (f.type ?? "").trim().toUpperCase(),
      a: f.qtyPerPartA || 0,
      b: f.qtyPerPartB || 0,
    })),
  });
}

export const ApplicationEditorModal = ({
  open,
  onClose,
  applicationId,
  mode,
  onSaved,
}: ApplicationEditorModalProps) => {
  const effectiveMode: Mode = mode ?? (applicationId ? "edit" : "create");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // C4: canonical snapshot of the loaded record so we can tell "no change" from
  // "changed" on save and offer update-vs-save-as-new.
  const originalSnapshotRef = useRef<string | null>(null);

  const detailQuery = useApplication(
    effectiveMode === "edit" ? (applicationId ?? null) : null,
    { enabled: open && effectiveMode === "edit" && !!applicationId },
  );
  const create = useCreateApplication();
  const selfUserId = useAppSelector((state) => state.auth.user?.id);
  const update = useUpdateApplication();

  // C2: staged-upload state (create mode only). `createdId` guards against a
  // duplicate create if a first save succeeded but some staged uploads failed —
  // a retry then PATCHes the already-created app instead of POSTing a new one.
  const uploadCad = useUploadCadStep();
  const uploadAttachment = useUploadAttachment();
  const [createdId, setCreatedId] = useState<string | null>(null);
  const crmFinalized = useRef(false);
  const [stagedCad, setStagedCad] = useState<File | null>(null);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploadingStaged, setUploadingStaged] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  const {
    fields,
    append,
    remove,
    update: updateFastener,
  } = useFieldArray({
    control,
    name: "fasteners",
  });

  // Only a listed material sets the required installation force (MICS-55).
  const { data: installationMaterials = NO_MATERIALS } = useInstallationMaterials();

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    if (effectiveMode === "edit" && detailQuery.data) {
      const formData = detailToFormData(detailQuery.data);
      reset(formData);
      originalSnapshotRef.current = canonicalizeForm(formData);
    } else if (effectiveMode === "create") {
      reset(EMPTY_DEFAULTS);
      originalSnapshotRef.current = null;
      // C2: fresh staging slate for a new application.
      setCreatedId(null);
      setStagedCad(null);
      setStagedFiles([]);
      setStageError(null);
    }
  }, [open, effectiveMode, detailQuery.data, reset]);

  // C2: pick/clear handlers for the staged (create-mode) CAD + attachments.
  const onStageCad = (file: File | null) => {
    setStageError(null);
    const filenameError = file ? cadFilenameValidationMessage(file.name) : null;
    if (filenameError) {
      setStageError(filenameError);
      return;
    }
    if (file && file.size > ATTACHMENT_MAX_BYTES) {
      setStageError(`"${file.name}" is over the 50 MB limit.`);
      return;
    }
    setStagedCad(file);
  };

  const onStageFiles = (files: File[]) => {
    setStageError(null);
    const tooLarge = files.filter((f) => f.size > ATTACHMENT_MAX_BYTES);
    const ok = files.filter((f) => f.size <= ATTACHMENT_MAX_BYTES);
    if (tooLarge.length > 0) {
      setStageError(
        `Skipped ${tooLarge.length} file${tooLarge.length === 1 ? "" : "s"} over the 50 MB limit: ${tooLarge
          .map((f) => f.name)
          .join(", ")}`,
      );
    }
    if (ok.length > 0) setStagedFiles((prev) => [...prev, ...ok]);
  };

  const onUnstageFile = (idx: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // C2: upload everything staged for `appId`; returns the names that failed so the
  // caller can keep them staged and let the user retry. Successful uploads are
  // dropped from the staged lists as they complete.
  const uploadStaged = useCallback(
    async (appId: string): Promise<string[]> => {
      const failures: string[] = [];
      setUploadingStaged(true);
      try {
        if (stagedCad) {
          try {
            await uploadCad.mutateAsync({
              applicationId: appId,
              file: stagedCad,
            });
            setStagedCad(null);
          } catch {
            failures.push(`3D part "${stagedCad.name}"`);
          }
        }
        const stillStaged: File[] = [];
        for (const file of stagedFiles) {
          try {
            await uploadAttachment.mutateAsync({ applicationId: appId, file });
          } catch {
            failures.push(`"${file.name}"`);
            stillStaged.push(file);
          }
        }
        setStagedFiles(stillStaged);
        return failures;
      } finally {
        setUploadingStaged(false);
      }
    },
    [stagedCad, stagedFiles, uploadCad, uploadAttachment],
  );

  // C4 (POC saveFromAppEditor): validate each
  // fastener Type against its brand catalog. PEM/PROFIL/AUTORIV/SHEREX must use a
  // known part; OTHER is free-text. A non-empty Type with no exact catalog match
  // blocks the save with a brand-specific message. A catalog outage never blocks
  // saving — the server stays the final authority.
  const validateFastenerTypes = useCallback(
    async (lines: ApplicationFormData["fasteners"]): Promise<string | null> => {
      const checks = lines
        .map((f) => ({ brand: f.brand, type: (f.type ?? "").trim() }))
        .filter((f) => f.brand !== "OTHER" && f.type.length > 0);
      for (const { brand, type } of checks) {
        try {
          const matches = await searchFastenerCatalog({
            brand,
            q: type,
            limit: 50,
          });
          const exact = matches.some(
            (m) => m.typeCode.toUpperCase() === type.toUpperCase(),
          );
          if (!exact) {
            return `Invalid ${labelForBrand(brand)} Type: "${type}". Please select an existing part from the list.`;
          }
        } catch {
          // Catalog lookup failed (transient/network). Don't block on a
          // validation outage; let svc-06 enforce on write.
        }
      }
      return null;
    },
    [],
  );

  const onSubmit = async (data: ApplicationFormData) => {
    setSubmitError(null);

    // C4: reject unknown fastener Types before writing.
    const typeError = await validateFastenerTypes(data.fasteners);
    if (typeError) {
      setSubmitError(typeError);
      return;
    }

    const fasteners: ApplicationFastenerLineInput[] = data.fasteners.map(
      (f) => {
        const a = f.qtyPerPartA > 0 ? f.qtyPerPartA : null;
        const b = f.qtyPerPartB > 0 ? f.qtyPerPartB : null;
        // API XOR: send qtyPerPart OR both qtyPerPartA+qtyPerPartB (not all three).
        // svc-06 persists qty_per_part = A+B when both sides are saved (POC total).
        // Collapse "A only" / "B only" to qtyPerPart for the single-side branch.
        if (a != null && b != null) {
          return {
            brand: f.brand,
            type: f.type,
            qtyPerPartA: a,
            qtyPerPartB: b,
          };
        }
        const single = a ?? b;
        return {
          brand: f.brand,
          type: f.type,
          qtyPerPart: single ?? undefined,
        };
      },
    );

    // C4: editing a saved application (POC:4197-4220). No changes → close without
    // an identical write. Changes → ask whether to update the existing record or
    // save a copy as a NEW application.
    let saveAsNew = false;
    let nameOverride: string | null = null;
    if (effectiveMode === "edit" && applicationId) {
      const original = originalSnapshotRef.current;
      const hasChanges =
        original == null || canonicalizeForm(data) !== original;
      if (!hasChanges) {
        onClose();
        return;
      }
      const updateExisting = window.confirm(
        "Changes detected in a saved application.\n\n" +
          "OK: Update the existing saved record\n" +
          "Cancel: Save as a NEW application",
      );
      if (!updateExisting) {
        const proposed = window.prompt(
          "Name for the new application:",
          data.name ?? "",
        );
        if (proposed == null) return; // user cancelled the save entirely
        const trimmed = proposed.trim();
        if (trimmed) nameOverride = trimmed;
        saveAsNew = true;
      }
    }

    const name = nameOverride ?? data.name;

    try {
      if (effectiveMode === "edit" && applicationId && !saveAsNew) {
        const body: UpdateApplicationInput = {
          name,
          applicationNumber: data.applicationNumber,
          oem: data.oem,
          productionProgram: data.productionProgram,
          materialCode: data.materialCode,
          annualVolume: data.annualVolume,
          batchSize: data.batchSize,
          fasteners,
        };
        const saved = await update.mutateAsync({ id: applicationId, body });
        onSaved?.(saved);
        onClose();
        return;
      }

      const body: CreateApplicationInput = {
        name,
        applicationNumber: data.applicationNumber,
        oem: data.oem,
        productionProgram: data.productionProgram,
        materialCode: data.materialCode,
        annualVolume: data.annualVolume,
        batchSize: data.batchSize,
        fasteners,
      };

      // Work-For (create only): stamp the current "working for" selection so the
      // app is owned by / tagged for the customer. Registered → workForUserId
      // (target owns); "Other" → workForEmail (creator owns, email stored). The
      // base `body` (no workFor) is reused for the PATCH branch below, whose
      // UpdateApplicationSchema is strict and rejects these keys.
      const wf = workForHeaderValues(readStoredWorkFor(), selfUserId ?? "");
      const createBody: CreateApplicationInput = {
        ...body,
        ...(wf.userId
          ? { workForUserId: wf.userId }
          : wf.email
            ? { workForEmail: wf.email }
            : {}),
      };

      if (saveAsNew) {
        // Save-as-new from an edited saved app — no staged files involved, so
        // the one submission goes immediately.
        const saved = await create.mutateAsync(createBody);
        onSaved?.(saved);
        await finalizeApplicationCrm(saved.applicationId).catch(() => {});
        onClose();
        return;
      }

      // C2 — pure create mode. Create the application (or PATCH the one a prior
      // save already created, when an earlier staged upload failed), then upload
      // any client-staged CAD + attachments now that we have an id.
      let appId = createdId;
      if (appId) {
        const saved = await update.mutateAsync({ id: appId, body });
        onSaved?.(saved);
      } else {
        const saved = await create.mutateAsync(createBody);
        appId = saved.applicationId;
        setCreatedId(appId);
        onSaved?.(saved);
      }

      const failures = await uploadStaged(appId);
      if (failures.length > 0) {
        setSubmitError(
          `Application saved, but ${failures.length} file${
            failures.length === 1 ? "" : "s"
          } couldn't be uploaded: ${failures.join(
            "; ",
          )}. They're still listed below — click Save to retry.`,
        );
        return; // keep the modal open so the user can retry the failed uploads
      }

      // One HubSpot submission per saved application, sent only once every
      // staged file is up so the single record carries them all. Guarded
      // because a retry re-enters this path with the same app already created.
      if (!crmFinalized.current) {
        crmFinalized.current = true;
        await finalizeApplicationCrm(appId).catch(() => {
          // Best-effort CRM step — never block or warn on the user's save.
        });
      }
      onClose();
    } catch (err) {
      setSubmitError(describeError(err));
    }
  };

  const isPending =
    create.isPending || update.isPending || isSubmitting || uploadingStaged;
  const isLoading = effectiveMode === "edit" && detailQuery.isLoading;

  const editTitle = (() => {
    const niceName = detailQuery.data?.name?.trim();
    return niceName ? `Edit: ${niceName}` : "Edit application";
  })();
  const modalTitle = effectiveMode === "edit" ? editTitle : "New application";

  const footer = (
    <div className="flex items-center justify-end gap-2">
      <PennButton type="button" variant="secondary" size="md" onClick={onClose}>
        Cancel
      </PennButton>
      <PennButton
        type="button"
        variant="primary"
        size="md"
        onClick={handleSubmit(onSubmit)}
        disabled={isPending || isLoading}
      >
        {uploadingStaged ? "Uploading files…" : isPending ? "Saving…" : "Save"}
      </PennButton>
    </div>
  );

  return (
    <PennModal
      open={open}
      onClose={onClose}
      // label="Application"
      title={modalTitle}
      subtitle="Edit part and fastener details, then save to use them for calculation and quotations."
      maxWidth="max-w-5xl"
      footer={footer}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-6 space-y-6"
        noValidate
      >
        {isLoading && (
          <PennText as="p" size="sm" color="slate-500">
            Loading application…
          </PennText>
        )}

        <section>
          <SectionLabel>Part information</SectionLabel>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
            <PennField
              label="Application / Panel name"
              htmlFor="app-name"
              required
              error={errors.name?.message}
            >
              <PennInput
                id="app-name"
                placeholder="e.g., Samplepart 1"
                error={!!errors.name}
                {...register("name")}
              />
            </PennField>
            <PennField label="Panel Part Number" htmlFor="app-number">
              <PennInput
                id="app-number"
                placeholder="e.g., A-001 / 12345"
                {...register("applicationNumber")}
              />
            </PennField>
            <PennField label="OEM (end customer)" htmlFor="app-oem">
              <PennInput
                id="app-oem"
                placeholder="e.g., BMW"
                {...register("oem")}
              />
            </PennField>
            <PennField label="Production program" htmlFor="app-program">
              <PennInput
                id="app-program"
                placeholder="e.g., Vehicle / platform / program"
                {...register("productionProgram")}
              />
            </PennField>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <PennField
              label="Annual production volume (parts/year)"
              htmlFor="app-volume"
              error={errors.annualVolume?.message}
            >
              <PennInput
                id="app-volume"
                type="number"
                min={0}
                placeholder="e.g., 200000"
                error={!!errors.annualVolume}
                {...register("annualVolume")}
              />
            </PennField>
            <PennField
              label="Average batch size (parts/batch)"
              htmlFor="app-batch-size"
              error={errors.batchSize?.message}
            >
              <PennInput
                id="app-batch-size"
                type="number"
                min={0}
                placeholder="e.g., 500"
                error={!!errors.batchSize}
                {...register("batchSize")}
              />
            </PennField>
            <PennField
              label="Material (part)"
              htmlFor="app-material"
              error={errors.materialCode?.message}
            >
              <Controller
                control={control}
                name="materialCode"
                render={({ field }) => (
                  <MaterialSelect
                    id="app-material"
                    value={field.value ?? null}
                    options={installationMaterials}
                    onChange={field.onChange}
                    error={!!errors.materialCode}
                  />
                )}
              />
            </PennField>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between gap-3">
            <div>
              <SectionLabel>Fastener information</SectionLabel>
              <PennText as="p" size="xs" color="slate-500" className="mt-0.5">
                Define the fasteners installed on this part.
              </PennText>
            </div>
            <PennButton
              type="button"
              variant="primary"
              size="md"
              onClick={() =>
                append({
                  brand: "PEM",
                  type: null,
                  qtyPerPartA: 0,
                  qtyPerPartB: 0,
                })
              }
            >
              Add fastener
            </PennButton>
          </div>

          <div className="mt-3 space-y-2">
            {fields.map((field, index) => (
              <FastenerRow
                key={field.id}
                index={index}
                control={control}
                register={register}
                setValue={setValue}
                errors={errors.fasteners?.[index]}
                isOnlyRow={fields.length <= 1}
                onRemove={() => {
                  // POC parity: the last remaining row is never removed — its
                  // values are cleared instead. Extra rows remove normally.
                  if (fields.length <= 1) {
                    updateFastener(index, makeEmptyFastener());
                  } else {
                    remove(index);
                  }
                }}
              />
            ))}
          </div>
        </section>

        {effectiveMode === "edit" && applicationId && (
          <section>
            <SectionLabel>3D part (optional)</SectionLabel>
            <PennText
              as="p"
              size="xs"
              color="slate-500"
              className="mt-0.5 mb-3"
            >
              Upload a STEP file so our engineers can review your part. A 3D
              preview is generated automatically.
            </PennText>
            {/* POC parity (issue 3): the 3D viewer link is NOT shown in the app
                editor — the preview/analysis entry point lives in the task view
                only. Here we keep upload + status, no "Open full viewer" link. */}
            <Cad3dPanel
              applicationId={applicationId}
              showViewer={false}
              showOpenViewerLink={false}
            />
          </section>
        )}

        {effectiveMode === "edit" && applicationId && (
          <section>
            <SectionLabel>Files (optional)</SectionLabel>
            <PennText
              as="p"
              size="xs"
              color="slate-500"
              className="mt-0.5 mb-3"
            >
              Attach supporting documents to this application.
            </PennText>
            <AttachmentsPanel applicationId={applicationId} />
          </section>
        )}

        {/* C2: create mode has no id yet, so CAD + files are staged in memory and
            uploaded automatically right after the first save. */}
        {effectiveMode === "create" && (
          <section>
            <SectionLabel>3D part (optional)</SectionLabel>
            <PennText
              as="p"
              size="xs"
              color="slate-500"
              className="mt-0.5 mb-3"
            >
              Attach a STEP file so our engineers can review your part. It
              uploads when you save this application, and a 3D preview is
              generated automatically.
            </PennText>
            <StagedCadField
              file={stagedCad}
              onPick={onStageCad}
              disabled={isPending}
            />
          </section>
        )}

        {effectiveMode === "create" && (
          <section>
            <SectionLabel>Files (optional)</SectionLabel>
            <PennText
              as="p"
              size="xs"
              color="slate-500"
              className="mt-0.5 mb-3"
            >
              Attach supporting documents now — they upload automatically when
              you save this application.
            </PennText>
            <StagedFilesField
              files={stagedFiles}
              onAdd={onStageFiles}
              onRemove={onUnstageFile}
              disabled={isPending}
            />
          </section>
        )}

        {stageError && (
          <div
            role="alert"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            {stageError}
          </div>
        )}

        {submitError && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {submitError}
          </div>
        )}
      </form>
    </PennModal>
  );
};

type MenuPosition = {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

// Fixed-position portal menu under its anchor (flips up when cramped); reports presses outside both.
function useAnchoredMenu(
  open: boolean,
  onOutside: () => void,
  desired: number,
  layoutKey: unknown,
) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;

  const computePos = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 4;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    // Flip up only when below is cramped and above genuinely has more room.
    const openUp =
      spaceBelow < Math.min(desired, 180) && spaceAbove > spaceBelow;
    if (openUp) {
      setPos({
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + margin,
        maxHeight: Math.max(120, Math.min(desired, spaceAbove)),
      });
    } else {
      setPos({
        left: rect.left,
        width: rect.width,
        top: rect.bottom + margin,
        maxHeight: Math.max(120, Math.min(desired, spaceBelow)),
      });
    }
  }, [desired]);

  useLayoutEffect(() => {
    if (!open) return;
    computePos();
    const reposition = () => computePos();
    // capture:true so scrolls inside the modal body are caught too.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, computePos, layoutKey]);

  useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        anchorRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      onOutsideRef.current();
    };
    document.addEventListener("mousedown", onDocPointerDown);
    return () => document.removeEventListener("mousedown", onDocPointerDown);
  }, [open]);

  return { anchorRef, menuRef, pos };
}

type FastenerTypeSelectProps = {
  value: string;
  onChange: (value: string | null) => void;
  onBlur: () => void;
  onQueryChange: (query: string) => void;
  options: FastenerCatalogItem[];
  isFreeText: boolean;
  error: boolean;
  brand: FastenerBrandCode | undefined;
};

const placeholderForBrand = (
  brand: FastenerBrandCode | undefined,
  isFreeText: boolean,
): string => {
  if (isFreeText) return "e.g., CLS / Blind rivet / ...";
  switch (brand) {
    case "PROFIL":
      return "Start typing (e.g., UMP... / EBF... / CSS...)";
    case "AUTORIV":
      return "Start typing (e.g., ACNM05 / STNM05(Stud) / ...)";
    case "SHEREX":
      return "Start typing (e.g., CAL2-470-2.0)";
    case "PEM":
    default:
      return "Start typing (e.g., SS / A4 / CLS ...)";
  }
};

/**
 * Brand-specific catalog hint shown under the Type field. Mirrors POC
 * — Sherex says "Part Number" instead of "catalog
 * entry" because Sherex types are looked up by part-number rather than a
 * separate catalog code. `null` for OTHER (free text — no catalog).
 */
type TypeHint = { brandLabel: string; entry: string; source: string };

const typeHintForBrand = (
  brand: FastenerBrandCode | undefined,
): TypeHint | null => {
  switch (brand) {
    case "PROFIL":
      return {
        brandLabel: "PROFIL",
        entry: "catalog entry",
        source: "profilFasteners.json",
      };
    case "AUTORIV":
      return {
        brandLabel: "Autoriv",
        entry: "catalog entry",
        source: "autorivFasteners.json",
      };
    case "SHEREX":
      return {
        brandLabel: "SHEREX",
        entry: "Part Number",
        source: "Sherex_Product_Info_With_Tooling_v2.json",
      };
    case "PEM":
      return {
        brandLabel: "PEM",
        entry: "catalog entry",
        source: "fasteners5.json",
      };
    default:
      return null;
  }
};

/**
 * Typeahead for the fastener type. The option list is rendered through a
 * portal with `position: fixed`, so it escapes the modal's `overflow-y-auto`
 * body (which previously clipped it / stacked it behind the dialog). It also
 * auto-flips above the input when there isn't enough room below — POC parity
 * for rows near the bottom of the dialog.
 */
const FastenerTypeSelect = ({
  value,
  onChange,
  onBlur,
  onQueryChange,
  options,
  isFreeText,
  error,
  brand,
}: FastenerTypeSelectProps) => {
  const [open, setOpen] = useState(false);
  // -1 = nothing highlighted, so Enter falls through to the form.
  const [highlighted, setHighlighted] = useState(-1);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const listId = useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;
  // 224 matches max-h-56.
  const { anchorRef, menuRef, pos } = useAnchoredMenu(
    open,
    () => setOpen(false),
    224,
    options.length,
  );

  const menuVisible = open && !isFreeText && options.length > 0 && pos != null;

  useEffect(() => {
    setHighlighted(-1);
  }, [options]);

  useEffect(() => {
    if (highlighted < 0) return;
    optionRefs.current[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const selectOption = (item: FastenerCatalogItem) => {
    onChange(item.typeCode);
    onQueryChange(item.typeCode);
    setOpen(false);
    setHighlighted(-1);
  };

  // Focus stays in the input (typeahead), so the highlight is virtual via
  // aria-activedescendant rather than the roving DOM focus MaterialSelect uses.
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isFreeText) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (options.length === 0) return;
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      if (!menuVisible) {
        setOpen(true);
        setHighlighted(step === 1 ? 0 : options.length - 1);
        return;
      }
      setHighlighted(
        (h) => (Math.max(h, -1) + step + options.length) % options.length,
      );
    } else if (e.key === "Enter") {
      if (!menuVisible || highlighted < 0) return;
      // Committing a suggestion must not submit the application form.
      e.preventDefault();
      selectOption(options[highlighted]);
    } else if (e.key === "Escape") {
      if (!menuVisible) return;
      e.preventDefault();
      setOpen(false);
      setHighlighted(-1);
    } else if (e.key === "Tab") {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  return (
    <div className="relative" ref={anchorRef}>
      <PennInput
        autoComplete="off"
        role="combobox"
        aria-expanded={menuVisible}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          menuVisible && highlighted >= 0 ? optionId(highlighted) : undefined
        }
        value={value}
        onChange={(e) => {
          onChange(e.target.value || null);
          onQueryChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholderForBrand(brand, isFreeText)}
        error={error}
      />
      {menuVisible &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-label="Fastener type"
            className="overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
            style={{
              position: "fixed",
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: pos.maxHeight,
              zIndex: 9999,
            }}
          >
            {options.map((item, i) => (
              <button
                key={`${item.source}:${item.typeCode}`}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                id={optionId(i)}
                type="button"
                role="option"
                aria-selected={i === highlighted}
                onMouseDown={(e) => {
                  // mouseDown (not click) so it fires before input blur.
                  e.preventDefault();
                  selectOption(item);
                }}
                onMouseEnter={() => setHighlighted(i)}
                className={`block w-full px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
                  i === highlighted
                    ? "bg-penn-100 text-penn-text"
                    : "text-penn-text hover:bg-penn-100"
                }`}
              >
                <span className="font-medium">{item.typeCode}</span>
                {item.typeDisplay && item.typeDisplay !== item.typeCode && (
                  <span className="ml-2 text-xs text-gray-400">
                    {item.typeDisplay}
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

type MaterialSelectProps = {
  id: string;
  value: string | null;
  options: readonly string[];
  onChange: (value: string | null) => void;
  error?: boolean;
};

// Listed materials plus an "Other" row that expands into free text inside the menu (MICS-55).
const MaterialSelect = ({ id, value, options, onChange, error }: MaterialSelectProps) => {
  const [open, setOpen] = useState(false);
  // null while the Other row is collapsed.
  const [draft, setDraft] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isOther = value !== null && !options.includes(value);

  const resolve = (text: string) => {
    const typed = text.trim();
    if (!typed) return null;
    return options.find((m) => m.toLowerCase() === typed.toLowerCase()) ?? typed;
  };
  const close = (commitDraft: boolean) => {
    if (commitDraft && draft !== null && draft.trim()) onChange(resolve(draft));
    setOpen(false);
    setDraft(null);
  };
  const choose = (next: string | null) => {
    onChange(next);
    setOpen(false);
    setDraft(null);
    triggerRef.current?.focus();
  };
  const openMenu = () => {
    setDraft(isOther ? value : null);
    setOpen(true);
  };
  const { anchorRef, menuRef, pos } = useAnchoredMenu(
    open,
    () => close(true),
    320,
    draft !== null,
  );

  const menuReady = open && pos != null;
  useEffect(() => {
    const menu = menuRef.current;
    if (!menuReady || !menu || menu.contains(document.activeElement)) return;
    (
      menu.querySelector<HTMLElement>('[aria-selected="true"]') ??
      menu.querySelector<HTMLElement>('[role="option"]')
    )?.focus();
  }, [menuReady, menuRef]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="option"], input') ?? [],
      );
      const step = e.key === "ArrowDown" ? 1 : -1;
      const at = items.indexOf(document.activeElement as HTMLElement);
      items[(Math.max(at, 0) + step + items.length) % items.length]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      close(false);
      triggerRef.current?.focus();
    } else if (e.key === "Tab") {
      // Hand focus back first so the browser tabs on from the trigger.
      close(true);
      triggerRef.current?.focus();
    }
  };

  const optionClass = (selected: boolean) =>
    `block w-full px-3 py-2 text-left text-sm transition-colors cursor-pointer ${
      selected ? "bg-penn-500 text-white" : "text-penn-text hover:bg-penn-100"
    }`;

  return (
    <div className="relative" ref={anchorRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close(true) : openMenu())}
        onKeyDown={(e) => {
          if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            openMenu();
          }
        }}
        className={`relative w-full rounded-xl border px-4 py-3 pr-10 text-left text-sm outline-none transition cursor-pointer focus:border-penn-500 ${
          error ? "border-error bg-error/5" : "border-gray-200 bg-white hover:border-penn-500"
        } ${value ? "text-penn-text" : "text-gray-400"}`}
      >
        <span className="block truncate">{value ?? "Select material"}</span>
        <svg
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.39a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open &&
        pos != null &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label="Material (part)"
            onKeyDown={onMenuKeyDown}
            className="overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
            style={{
              position: "fixed",
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: pos.maxHeight,
              zIndex: 9999,
            }}
          >
            {[null, ...options].map((material) => (
              <button
                key={material ?? ""}
                type="button"
                role="option"
                aria-selected={value === material}
                onClick={() => choose(material)}
                className={optionClass(value === material)}
              >
                {material ?? "Not specified"}
              </button>
            ))}
            <div className="mt-1 border-t border-gray-100 pt-1">
              {draft === null ? (
                <button
                  type="button"
                  role="option"
                  aria-selected={isOther}
                  onClick={() => setDraft("")}
                  className={optionClass(isOther)}
                >
                  Other (enter manually)
                </button>
              ) : (
                <div className="px-3 py-2">
                  <PennInput
                    autoFocus
                    aria-label="Other material"
                    maxLength={MATERIAL_MAX_CHARS}
                    placeholder="Enter material"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        choose(resolve(draft));
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

type FastenerRowProps = {
  index: number;
  control: ReturnType<typeof useForm<ApplicationFormData>>["control"];
  register: ReturnType<typeof useForm<ApplicationFormData>>["register"];
  setValue: ReturnType<typeof useForm<ApplicationFormData>>["setValue"];
  errors:
    | {
        brand?: { message?: string };
        type?: { message?: string };
        qtyPerPartA?: { message?: string };
        qtyPerPartB?: { message?: string };
      }
    | undefined;
  isOnlyRow: boolean;
  onRemove: () => void;
};

const FastenerRow = ({
  index,
  control,
  register,
  setValue,
  errors,
  isOnlyRow,
  onRemove,
}: FastenerRowProps) => {
  const [typeQuery, setTypeQuery] = useState("");
  const brand = useWatch({
    control,
    name: `fasteners.${index}.brand` as const,
  }) as FastenerBrandCode | undefined;

  const catalog = useFastenerCatalog(brand ?? null, typeQuery);
  const typeOptions = catalog.data ?? [];
  const isFreeText = brand === "OTHER";
  const typeHint = typeHintForBrand(brand);

  return (
    <div className="p-3 rounded-xl border border-gray-200 bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-3">
          <Controller
            control={control}
            name={`fasteners.${index}.brand` as const}
            render={({ field }) => (
              <PennField
                label={index === 0 ? "Brand" : undefined}
                error={errors?.brand?.message}
              >
                <PennDropdown
                  options={BRAND_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                  value={field.value ?? ""}
                  onChange={(v) => {
                    const next = v as FastenerBrandCode;
                    if (next !== field.value) {
                      // POC parity: switching
                      // brand resets the row's Type since the catalog source
                      // changes per brand. We also clear the typeahead query
                      // so the next dropdown reflects the new brand's items.
                      setValue(`fasteners.${index}.type`, null, {
                        shouldDirty: true,
                        shouldValidate: false,
                      });
                      setTypeQuery("");
                    }
                    field.onChange(next);
                  }}
                  onBlur={field.onBlur}
                  error={!!errors?.brand}
                />
              </PennField>
            )}
          />
        </div>

        <div className="md:col-span-4">
          <Controller
            control={control}
            name={`fasteners.${index}.type` as const}
            render={({ field }) => (
              <PennField
                label={index === 0 ? "Type" : undefined}
                error={errors?.type?.message}
              >
                <FastenerTypeSelect
                  value={field.value ?? ""}
                  onChange={(v) => field.onChange(v)}
                  onBlur={field.onBlur}
                  onQueryChange={setTypeQuery}
                  options={typeOptions}
                  isFreeText={isFreeText}
                  error={!!errors?.type}
                  brand={brand}
                />
              </PennField>
            )}
          />
          {!isFreeText && typeHint && (
            <p className="mt-1 text-xs text-slate-500">
              {typeHint.brandLabel}: Type must match an existing{" "}
              <span className="font-semibold">{typeHint.entry}.</span>
              {/* (from{" "}
              {typeHint.source}). */}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <PennField
            label={index === 0 ? "Qty / part (A-side)" : undefined}
            error={errors?.qtyPerPartA?.message}
          >
            <PennInput
              type="number"
              step="any"
              min={0}
              placeholder="e.g., 12"
              error={!!errors?.qtyPerPartA}
              {...register(`fasteners.${index}.qtyPerPartA` as const)}
            />
          </PennField>
        </div>

        <div className="md:col-span-2">
          <PennField
            label={index === 0 ? "Qty / part (B-side)" : undefined}
            error={errors?.qtyPerPartB?.message}
          >
            <PennInput
              type="number"
              step="any"
              min={0}
              placeholder="e.g., 12"
              error={!!errors?.qtyPerPartB}
              {...register(`fasteners.${index}.qtyPerPartB` as const)}
            />
          </PennField>
          <p className="mt-1 text-xs text-slate-500">
            Only relevant when producing on T-Series.
          </p>
        </div>

        <div className="md:col-span-1 flex justify-end">
          <button
            type="button"
            onClick={onRemove}
            className={`h-9 w-9 ${index === 0 ? "md:mt-6" : ""} flex items-center justify-center rounded-lg border border-gray-200 text-red-400 hover:bg-red-50 transition-colors cursor-pointer`}
            title={isOnlyRow ? "Clear fastener" : "Remove fastener"}
            aria-label={isOnlyRow ? "Clear fastener" : "Remove fastener"}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <PennText
    as="p"
    size="xs"
    color="slate-500"
    font="semibold"
    className="uppercase"
  >
    {children}
  </PennText>
);

// C2: presentational staged-CAD picker (create mode). Holds no hooks — the parent
// keeps the File in state and uploads it after the first save.
type StagedCadFieldProps = {
  file: File | null;
  onPick: (file: File | null) => void;
  disabled?: boolean;
};

const StagedCadField = ({ file, onPick, disabled }: StagedCadFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <PennButton
          type="button"
          variant="secondary"
          size="md"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          {file ? "Replace STEP file" : "Choose STEP file"}
        </PennButton>
        <input
          ref={inputRef}
          type="file"
          accept={CAD_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            e.target.value = ""; // allow re-selecting the same file
            onPick(f);
          }}
        />
        {file && (
          <>
            <PennText as="span" size="xs" color="blue_grey">
              {file.name} ({formatStagedSize(file.size)})
            </PennText>
            <PennButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onPick(null)}
              disabled={disabled}
            >
              Remove
            </PennButton>
          </>
        )}
      </div>
      <PennText as="p" size="xs" color="blue_grey">
        Accepts STEP files (.step, .stp), up to 50&nbsp;MB, with filenames up to
        {CAD_FILENAME_MAX_CHARS} characters. Uploaded when you save.
      </PennText>
    </div>
  );
};

// C2: presentational staged-attachments picker (create mode).
type StagedFilesFieldProps = {
  files: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
};

const StagedFilesField = ({
  files,
  onAdd,
  onRemove,
  disabled,
}: StagedFilesFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [zipping, setZipping] = useState(false);
  const onDownloadAll = async () => {
    setZipping(true);
    try {
      // Issue 4b: the new-app popup has no server copy yet (C2 staging), so we
      // zip the in-memory File objects directly — no network, no S3 CORS.
      await downloadFilesAsZip(files);
    } finally {
      setZipping(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <PennButton
          type="button"
          variant="secondary"
          size="md"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          Choose files
        </PennButton>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = ""; // allow re-selecting the same file(s)
            if (picked.length > 0) onAdd(picked);
          }}
        />
      </div>
      <PennText as="p" size="xs" color="blue_grey">
        Attach supporting documents (drawings, specs, photos), up to 50&nbsp;MB
        each. Uploaded when you save.
      </PennText>
      {files.length > 0 && (
        <>
          {/* POC parity (issue 4): "Download all" in the files header. */}
          <div className="flex items-center justify-between gap-2">
            <PennText
              as="span"
              size="xs"
              color="blue_grey"
              className="uppercase font-semibold"
            >
              Staged files
            </PennText>
            <PennButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void onDownloadAll()}
              disabled={zipping}
            >
              {zipping ? "Preparing ZIP…" : "Download all"}
            </PennButton>
          </div>
          <ul className="divide-y divide-gray-100 rounded-2xl border border-gray-200">
            {files.map((f, idx) => (
              <li
                key={`${f.name}-${idx}`}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0">
                  <PennText
                    as="p"
                    size="sm"
                    className="truncate font-medium text-gray-800"
                  >
                    {f.name}
                  </PennText>
                  <PennText as="span" size="xs" color="blue_grey">
                    {formatStagedSize(f.size)}
                  </PennText>
                </div>
                {/* Per-file actions parity with the saved-application
                  AttachmentsPanel: every staged file gets its own Download
                  (zero-network, the File is already in memory) plus Remove. */}
                <div className="flex shrink-0 items-center gap-2">
                  <PennButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => downloadStagedFile(f)}
                    disabled={disabled}
                  >
                    Download
                  </PennButton>
                  <PennButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(idx)}
                    disabled={disabled}
                  >
                    Remove
                  </PennButton>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
